export default {
  methods: {
    hasCoordinates(latitude, longitude) {
      return (
        Number.isFinite(latitude) &&
        Number.isFinite(longitude) &&
        !(latitude === 0 && longitude === 0) &&
        latitude >= -90 &&
        latitude <= 90 &&
        longitude >= -180 &&
        longitude <= 180
      )
    },

    getCurrentPositionOnce() {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocation is not supported by this browser'))
          return
        }

        navigator.geolocation.getCurrentPosition(
          position => {
            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude
            })
          },
          error => reject(error),
          {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 0
          }
        )
      })
    },

    async getExifCoordinates(file) {
      if (!file) return null

      try {
        const buffer = await file.arrayBuffer()
        return this.parseExifGpsCoordinates(buffer)
      } catch (error) {
        console.error(error)
        return null
      }
    },

    parseExifGpsCoordinates(buffer) {
      const view = new DataView(buffer)

      if (view.byteLength < 4 || view.getUint16(0, false) !== 0xffd8) {
        return null
      }

      let offset = 2

      while (offset + 4 < view.byteLength) {
        if (view.getUint8(offset) !== 0xff) return null

        const marker = view.getUint8(offset + 1)
        const segmentLength = view.getUint16(offset + 2, false)

        if (marker === 0xe1 && this.isExifSegment(view, offset + 4)) {
          return this.readExifGpsFromSegment(view, offset + 10)
        }

        offset += 2 + segmentLength
      }

      return null
    },

    isExifSegment(view, offset) {
      return (
        view.getUint8(offset) === 0x45 &&
        view.getUint8(offset + 1) === 0x78 &&
        view.getUint8(offset + 2) === 0x69 &&
        view.getUint8(offset + 3) === 0x66 &&
        view.getUint8(offset + 4) === 0x00 &&
        view.getUint8(offset + 5) === 0x00
      )
    },

    readExifGpsFromSegment(view, tiffStart) {
      const littleEndian =
        view.getUint8(tiffStart) === 0x49 &&
        view.getUint8(tiffStart + 1) === 0x49

      if (view.getUint16(tiffStart + 2, littleEndian) !== 42) return null

      const firstIfdOffset = view.getUint32(tiffStart + 4, littleEndian)
      const gpsIfdOffset = this.findGpsIfdOffset(view, tiffStart, firstIfdOffset, littleEndian)
      if (!gpsIfdOffset) return null

      return this.readGpsIfd(view, tiffStart, gpsIfdOffset, littleEndian)
    },

    findGpsIfdOffset(view, tiffStart, ifdOffset, littleEndian) {
      const ifdStart = tiffStart + ifdOffset
      const entryCount = view.getUint16(ifdStart, littleEndian)

      for (let index = 0; index < entryCount; index += 1) {
        const entryOffset = ifdStart + 2 + (index * 12)
        const tag = view.getUint16(entryOffset, littleEndian)

        if (tag === 0x8825) {
          return view.getUint32(entryOffset + 8, littleEndian)
        }
      }

      return null
    },

    readGpsIfd(view, tiffStart, gpsIfdOffset, littleEndian) {
      const gpsIfdStart = tiffStart + gpsIfdOffset
      const entryCount = view.getUint16(gpsIfdStart, littleEndian)
      let latitudeRef = ''
      let longitudeRef = ''
      let latitudeValues = null
      let longitudeValues = null

      for (let index = 0; index < entryCount; index += 1) {
        const entryOffset = gpsIfdStart + 2 + (index * 12)
        const tag = view.getUint16(entryOffset, littleEndian)

        if (tag === 0x0001) latitudeRef = this.readExifAscii(view, entryOffset, littleEndian)
        if (tag === 0x0002) latitudeValues = this.readExifRationals(view, tiffStart, entryOffset, littleEndian)
        if (tag === 0x0003) longitudeRef = this.readExifAscii(view, entryOffset, littleEndian)
        if (tag === 0x0004) longitudeValues = this.readExifRationals(view, tiffStart, entryOffset, littleEndian)
      }

      if (!latitudeValues || !longitudeValues) return null

      const latitude = this.convertGpsCoordinate(latitudeValues, latitudeRef)
      const longitude = this.convertGpsCoordinate(longitudeValues, longitudeRef)

      return this.hasCoordinates(latitude, longitude)
        ? { latitude, longitude }
        : null
    },

    readExifAscii(view, entryOffset, littleEndian) {
      const count = view.getUint32(entryOffset + 4, littleEndian)
      const valueOffset = entryOffset + 8
      let value = ''

      for (let index = 0; index < Math.min(count, 4); index += 1) {
        const charCode = view.getUint8(valueOffset + index)
        if (charCode) value += String.fromCharCode(charCode)
      }

      return value
    },

    readExifRationals(view, tiffStart, entryOffset, littleEndian) {
      const count = view.getUint32(entryOffset + 4, littleEndian)
      const valueOffset = tiffStart + view.getUint32(entryOffset + 8, littleEndian)
      const values = []

      for (let index = 0; index < count; index += 1) {
        const rationalOffset = valueOffset + (index * 8)
        const numerator = view.getUint32(rationalOffset, littleEndian)
        const denominator = view.getUint32(rationalOffset + 4, littleEndian)
        values.push(denominator ? numerator / denominator : 0)
      }

      return values
    },

    convertGpsCoordinate(values, ref) {
      const coordinate = values[0] + (values[1] / 60) + (values[2] / 3600)
      return ['S', 'W'].includes(ref?.trim()) ? -coordinate : coordinate
    },

  },
}
